import { colors } from '@/styles';
import { pixelRect, pixelMask } from '@/utils/tilePixels';
import { Signal } from '@/models/data/Signal';
import { TileType } from '@/models/TileTypes';
import {
  defaultTileGraphics,
  connectionGraphics,
} from '@/models/tiles/DefaultTileGraphics';
import { TileDefinition, TileGraphicsDefinition } from '@/models/tiles/TileDefinition';
import { Side, TileSideIOType } from '@/schemas/tiles';
import { standardBuildAndConnect } from '@/models/logic/utils';

interface DelayQueueItem {
  signal: Signal;
  timeStarted: number;
}

export interface DelayTileState {
  delayTimeMs: number;
  queue: DelayQueueItem[];
  lastInput: Signal | null;
  currentOutput: Signal;
}

export class DelayTile implements TileDefinition<DelayTileState> {
  type = TileType.Delay;
  name = 'Delay';
  canRotate = true;
  graphics = delayTileGraphicsDefinition;

  createDefaultState = () => ({
    delayTimeMs: 1_000,
    queue: [],
    lastInput: null,
    currentOutput: Signal.LOW,
  });
  buildAndConnectNodes: NonNullable<
    TileDefinition<DelayTileState>['buildAndConnectNodes']
  > = (data) => {
    const queue = standardBuildAndConnect('DELAY', data, this);
    const node = data.graph.nodes.get(data.instance.logicNodeIds![Side.TOP]!);
    const duration = (data.instance.state as DelayTileState).delayTimeMs;
    if (node)
      node.state.delayMs = Number.isFinite(duration) && duration > 0 ? duration : 1000;
    return queue;
  };

  ioSides = {
    [Side.TOP]: { type: TileSideIOType.NONE, forceConnection: false },
    [Side.RIGHT]: { type: TileSideIOType.OUTPUT, forceConnection: true },
    [Side.BOTTOM]: { type: TileSideIOType.NONE, forceConnection: false },
    [Side.LEFT]: { type: TileSideIOType.INPUT, forceConnection: true },
  };
}

export const delayTileGraphicsDefinition: TileGraphicsDefinition<DelayTileState> = {
  type: TileType.Delay,
  draw: function (g, { state, meta, connections, context }): void {
    defaultTileGraphics(g);

    const ioSides = {
      [Side.TOP]: { type: TileSideIOType.NONE, forceConnection: false },
      [Side.RIGHT]: { type: TileSideIOType.OUTPUT, forceConnection: true },
      [Side.BOTTOM]: { type: TileSideIOType.NONE, forceConnection: false },
      [Side.LEFT]: { type: TileSideIOType.INPUT, forceConnection: true },
    };

    connectionGraphics(
      g,
      colors.signalColor,
      colors.noSignalColor,
      meta,
      connections,
      ioSides,
      context,
    );

    pixelRect(g, 1, 7, 23, 11, 0x000000);
    pixelRect(
      g,
      3,
      9,
      19,
      7,
      meta.outputs.right === Signal.HIGH ? colors.signalColor : colors.noSignalColor,
    );
    // Quantize the time display; drawing never alters the queue or timing semantics.
    const duration =
      Number.isFinite(state.delayTimeMs) && state.delayTimeMs > 0
        ? state.delayTimeMs
        : 1000;
    // FIFO order: later transitions overwrite the prefix nearest the input.
    // Their trailing edges leave older transitions visible farther to the right.
    for (const item of state.queue) {
      const elapsed = Math.max(0, Math.min(duration, Date.now() - item.timeStarted));
      const end = Math.round((elapsed / duration) * 19);
      pixelRect(
        g,
        3,
        9,
        end,
        7,
        item.signal === Signal.HIGH ? colors.signalColor : colors.noSignalColor,
      );
    }
    // Center the point's 5×7 bounds on the tile, keeping FIFO bands visible.
    pixelMask(
      g,
      [
        '....##...',
        '....###..',
        '....####.',
        '....#####',
        '....####.',
        '....###..',
        '....##...',
      ],
      6,
      9,
      0x000000,
    );
  },
};
